import React, { useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ShieldCheck, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../helpers/useAuth';
import { useBranding } from '../helpers/useBranding';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/Tabs';
import { PasswordLoginForm } from '../components/PasswordLoginForm';
import { PasswordRegisterForm } from '../components/PasswordRegisterForm';
import { OAuthButtonGroup } from '../components/OAuthButtonGroup';
import styles from './login.module.css';

const valueProps = [
  'Secure session-level collaboration',
  'Multi-agent coding workflows',
  'Built for production teams and client delivery',
];

export default function LoginPage() {
  const { authState } = useAuth();
  const { branding, isWhiteLabeled } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = useMemo(() => {
    const next = new URLSearchParams(location.search).get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) return next;
    return '/dashboard';
  }, [location.search]);

  useEffect(() => {
    if (authState.type === 'authenticated') {
      navigate(redirectTo, { replace: true });
    }
  }, [authState, navigate, redirectTo]);

  if (authState.type === 'authenticated') {
    return null;
  }

  return (
    <div className={styles.container}>
      <>
        <title>Login | {branding.brandName}</title>
        <meta
          name="description"
          content={`Sign in to ${branding.brandName} to launch secure multi-agent coding sessions.`}
        />
      </>

      <div className={styles.shell}>
        <section className={styles.brandPanel}>
          <div className={styles.logoRow}>
            <img src={branding.logoUrl} alt={branding.brandName} className={styles.logoIcon} />
            <h1 className={styles.title}>
              {isWhiteLabeled ? (
                <span>{branding.brandName}</span>
              ) : (
                <>
                  <span>tasking</span>
                  <span className={styles.techSuffix}>.tech</span>
                </>
              )}
            </h1>
          </div>
          <p className={styles.subtitle}>
            {isWhiteLabeled
              ? `Welcome to ${branding.brandName}. Sign in to access your AI-powered workspace.`
              : 'Enter your workspace where AI-assisted build sessions turn engineering throughput into real delivery outcomes.'}
          </p>

          <div className={styles.pillRow}>
            <span className={styles.pill}>
              <Sparkles size={14} />
              Live AI collaboration
            </span>
            <span className={styles.pill}>
              <ShieldCheck size={14} />
              Enterprise-grade controls
            </span>
          </div>

          <ul className={styles.valueList}>
            {valueProps.map((item) => (
              <li key={item} className={styles.valueItem}>
                <ArrowRight size={14} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.authPanel}>
          <div className={styles.mobileLogoRow}>
            <img src={branding.logoUrl} alt={branding.brandName} className={styles.mobileLogoIcon} />
            <h1 className={styles.mobileTitle}>
              {isWhiteLabeled ? (
                <span>{branding.brandName}</span>
              ) : (
                <>
                  <span>tasking</span>
                  <span className={styles.techSuffix}>.tech</span>
                </>
              )}
            </h1>
          </div>
          <div className={styles.authPanelHead}>
            <h2>Welcome back</h2>
            <p>Sign in or create your account to continue.</p>
          </div>

          <div className={styles.authBox}>
            <Tabs defaultValue="login" className={styles.tabs}>
              <TabsList className={styles.tabsList}>
                <TabsTrigger value="login" className={styles.tabTrigger}>
                  Login
                </TabsTrigger>
                <TabsTrigger value="register" className={styles.tabTrigger}>
                  Register
                </TabsTrigger>
              </TabsList>

              <div className={styles.tabContentWrapper}>
                <TabsContent value="login" className={styles.tabContent}>
                  <PasswordLoginForm redirectTo={redirectTo} />
                  <OAuthButtonGroup />
                </TabsContent>
                <TabsContent value="register" className={styles.tabContent}>
                  <PasswordRegisterForm redirectTo={redirectTo} />
                  <OAuthButtonGroup />
                </TabsContent>
              </div>
            </Tabs>
          </div>

          <div className={styles.footer}>
            <Link to="/" className={styles.backLink}>
              &larr; Back to Home
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
